import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { FETCH_BOOKMARKED_DOCUMENTS } from '../../graphql/queries/documents';
import { fetchUser } from '@src/store/user';
import * as api from '@src/utils/mmc-api/api';
import { FaTrashAlt } from 'react-icons/fa';
import { DocumentLink } from '../DocumentLink';

type Category = {
  slug: string;
  name: string;
};

type Module = {
  slug: string;
  title: string;
};

type Document = {
  id: string;
  name?: string;
  displayName?: string;
  fileUrl: string;
  externalUrl?: string;
  price?: number;
  hasPassword?: boolean;
  freeDocument?: boolean;
  freeSample?: boolean;
  isSubscription?: boolean;
  videoSolution?: string;
  module?: Module;
  documentCategory?: Category[];
};

type GroupedDocuments = Map<string, Map<string, Document[]>>;

type BookmarkListProps = {
  ids: string[];
};

function groupByModuleAndCategory(documents: Document[]): GroupedDocuments {
  const grouped: GroupedDocuments = new Map();

  for (const doc of documents) {
    const moduleTitle = doc.module?.title ?? 'Other';
    const categoryName = doc.documentCategory?.[0]?.name ?? 'Uncategorised';

    if (!grouped.has(moduleTitle)) {
      grouped.set(moduleTitle, new Map());
    }

    const moduleGroup = grouped.get(moduleTitle)!;
    if (!moduleGroup.has(categoryName)) {
      moduleGroup.set(categoryName, []);
    }

    moduleGroup.get(categoryName)!.push(doc);
  }

  return grouped;
}

export const BookmarkList: React.FC<BookmarkListProps> = ({ ids }) => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user.data);
  const isSubscribed = user?.subscribed ?? false;
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());

  const { data, previousData, loading } = useQuery(FETCH_BOOKMARKED_DOCUMENTS, {
    variables: { ids },
    skip: ids.length === 0,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const documents: Document[] = data?.allDocuments ?? previousData?.allDocuments ?? [];

  const visibleDocuments = useMemo(() => {
    if (removedIds.size === 0) return documents;
    return documents.filter((doc) => !removedIds.has(doc.id));
  }, [documents, removedIds]);

  const grouped = useMemo(() => groupByModuleAndCategory(visibleDocuments), [visibleDocuments]);

  const handleRemove = async (docId: string) => {
    setRemovedIds((prev) => new Set(prev).add(docId));

    try {
      await api.removeBookmark({ document_id: docId });
      dispatch(fetchUser());
    } catch {
      setRemovedIds((prev) => {
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
    }
  };

  if (!loading && visibleDocuments.length === 0) {
    return <p>No bookmarked documents.</p>;
  }

  return (
    <div className="bookmark-list">
      {[...grouped.entries()].map(([moduleTitle, categories]) => (
        <section key={moduleTitle} className="bookmark-list__module">
          <h2 className="bookmark-list__module-title">{moduleTitle}</h2>

          {[...categories.entries()].map(([categoryName, docs]) => (
            <div key={categoryName} className="bookmark-list__category">
              <h3 className="bookmark-list__category-title">{categoryName}</h3>

              {docs.map((doc) => (
                <DocumentLink
                  key={doc.id}
                  document={doc}
                  isSubscribed={isSubscribed}
                  fallbackPrice=""
                  action={
                    <button
                      className="bookmark-list__remove-btn"
                      onClick={() => handleRemove(doc.id)}
                      aria-label="Remove bookmark"
                    >
                      <FaTrashAlt size={21} />
                    </button>
                  }
                />
              ))}
            </div>
          ))}
        </section>
      ))}
    </div>
  );
};
